"use client";
import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Clock,
  User,
  MessageSquare,
  Search,
  X,
  Plus,
  Edit,
  Trash2,
  Eye,
  ChevronsLeft,
  ChevronsRight,
  Shield,
  Users,
  HardHat,
  DollarSign,
  Loader2,
  Accessibility
} from 'lucide-react';

import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useComplaintTypes } from '../../context/ComplaintTypeContext';
import { useDepartments } from '../../context/DepartmentContext';
import { useEmployee } from '../../context/EmployeeContext';
import { useComplaints } from '../../context/ComplaintsContext';
import { CDN_URL } from '../../config/apiConfig';
import { useAuth } from "../../context/AuthContext";

// ── Constants ────────────────────────────────────────────────────────────────
const INITIAL_FORM = {
  title: "",
  description: "",
  category: "",
  priority: "medium",
  status: "open",

  reportedBy: "",
  assignedTo: [],
  attachments: [],
  comments: "",

  isAnonymous: 0,
  is_anonymous: false,
  is_posh: false,
};

const PRIORITY_OPTIONS = ["low", "medium", "high"];

const STATUS_OPTIONS = ["open", "investigating", "resolved", "closed"];

const STATUS_LABEL = {
  open: "Pending Review",
  investigating: "Under Investigation",
  resolved: "Resolved",
  closed: "Closed"
};

const STATUS_CLASS = {
  open: "bg-yellow-100 text-yellow-700",
  investigating: "bg-blue-100 text-blue-700",
  resolved: "bg-green-100 text-green-700",
  closed: "bg-gray-100 text-gray-700"
};

const STATUS_DOT = {
  open: "bg-yellow-500",
  investigating: "bg-blue-500",
  resolved: "bg-green-500",
  closed: "bg-gray-500"
};

const PRIORITY_CLASS = {
  low: "bg-green-100 text-green-700",
  medium: "bg-yellow-100 text-yellow-700",
  high: "bg-red-100 text-red-700"
};

const PRIORITY_DOT = {
  low: "bg-green-500",
  medium: "bg-yellow-500",
  high: "bg-red-500"
};

const CATEGORY_ICONS = {
  harassment: Shield,
  discrimination: Users,
  safety: HardHat,
  compensation: DollarSign,
  accessibility: Accessibility
};

const COMMENT_LIMIT = 60;

// ── Sub-components ────────────────────────────────────────────────────────────
function CommentCell({ text }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const isLong = text.length > COMMENT_LIMIT;
  return (
    <span>
      {isLong && !expanded ? text.slice(0, COMMENT_LIMIT) + "…" : text}
      {isLong && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="ml-1 text-blue-600 text-xs font-semibold underline underline-offset-2 hover:text-blue-800 focus:outline-none whitespace-nowrap"
        >
          {expanded ? "View less" : "View more"}
        </button>
      )}
    </span>
  );
}

// ── Avatar helpers ────────────────────────────────────────────────────────────
const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0][0]?.toUpperCase();
  return parts[0][0]?.toUpperCase() + parts[parts.length - 1][0]?.toUpperCase();
};

const AVATAR_COLORS = [
  "bg-indigo-500",
  "bg-green-500",
  "bg-blue-500",
  "bg-pink-500",
  "bg-yellow-500",
  "bg-sky-500",
  "bg-red-500",
  "bg-teal-500",
];

const Avatar = ({ name }) => {
  const fullName = name || "Unknown";

  let hash = 0;
  for (let i = 0; i < fullName.length; i++) {
    hash = fullName.charCodeAt(i) + ((hash << 5) - hash);
  }

  const bg = AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];

  return (
    <div className="relative group inline-flex">
      <div
        className={`w-7 h-7 flex items-center justify-center rounded-full text-white text-xs font-bold ${bg}`}
      >
        {getInitials(fullName)}
      </div>

      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-black text-white text-[11px] px-2 py-1 rounded z-50 whitespace-normal max-w-[200px] break-words text-center">
        {fullName}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function WorkplaceIssues() {
  const {complaintTypes, loading: complaintLoading} = useComplaintTypes();
  const { canDo } = useAuth();

  const { departments } = useDepartments();
  const { allEmployees } = useEmployee();
  const { complaints, loading, createComplaint, updateComplaint, deleteComplaint } = useComplaints();

  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);

  // ── State ─────────────────────────────────────────────────────────────────
  const issues = complaints;
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("create");
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);

  // ── Filter state ─────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterPriority, setFilterPriority] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // ── Pagination ───────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [issueloading, setLoading] = useState(false);

  const user = JSON.parse(localStorage.getItem("user"));
const isAdmin = user?.role === "Admin";

const isView = modalType === "view";

  // ── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterStatus, filterCategory, filterPriority, fromDate, toDate]);

  // ── Base filtered rows ───────────────────────────────────────────────────
  const q = searchTerm.toLowerCase();
  const baseRows = React.useMemo(
    () =>
      issues.filter((issue) => {
        const activityDate = (
  issue.update_audit_time ||
  issue.create_audit_time ||
  ""
).split(" ")[0];
        return (
          (filterCategory === "All" ||
            String(issue.complaint_type_id) === String(filterCategory)) &&
          (filterPriority === "All" || issue.priority === filterPriority) &&
          (!fromDate || activityDate >= fromDate) &&
          (!toDate || activityDate <= toDate) &&
          (String(issue.id).toLowerCase().includes(q) ||
            issue.type_name?.toLowerCase().includes(q) ||
            issue.title?.toLowerCase().includes(q) ||
            issue.description?.toLowerCase().includes(q))
        );
      }),
    [issues, searchTerm, filterCategory, filterPriority, fromDate, toDate]
  );

  // ── Status counts ────────────────────────────────────────────────────────
  const statusCounts = React.useMemo(() => {
    const counts = {};
    STATUS_OPTIONS.forEach((s) => {
      counts[s] = 0;
    });
    baseRows.forEach((issue) => {
      if (counts[issue.status] !== undefined) counts[issue.status]++;
    });
    return counts;
  }, [baseRows]);

  // ── Status-filtered rows ─────────────────────────────────────────────────
  const filteredRows = React.useMemo(() => {
    return baseRows.filter(
      (issue) =>
        filterStatus.length === 0 || filterStatus.includes(issue.status)
    );
  }, [baseRows, filterStatus]);

  const totalPages = Math.ceil(filteredRows.length / rowsPerPage) || 1;
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [filteredRows.length, rowsPerPage]);

  const currentRows = filteredRows.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // ── Form helpers ──────────────────────────────────────────────────────────
  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const resetFormState = () => {
    setForm(INITIAL_FORM);
  };

  const closeModal = () => {
    resetFormState();
    setShowModal(false);
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
  e.preventDefault();

  setLoading(true);

  try {
  const loginUser = JSON.parse(localStorage.getItem("user"));

  const formData = new FormData();

  formData.append("complaint_type_id", form.category);
  formData.append("title", form.title);
  formData.append("description", form.description);

  formData.append(
    "raised_by",
    form.reportedBy || loginUser?.id || ""
  );

  // ✅ FIXED: send proper JSON array
  formData.append(
    "against_user_id",
    JSON.stringify(form.assignedTo || [])
  );

  formData.append("priority", form.priority);

  formData.append(
  "is_anonymous",
  form.is_anonymous ? 1 : 0
);

formData.append(
  "is_posh",
  form.is_posh ? 1 : 0
);

  formData.append("status", form.status);

  formData.append(
    "create_audit_id",
    loginUser?.id || ""
  );

  if (form.attachments?.length > 0) {
    formData.append("file", form.attachments[0]);
  }

  let response;

  if (modalType === "create") {
    response = await createComplaint(formData);
  } else {
    formData.append(
      "update_audit_id",
      loginUser?.id || ""
    );

    response = await updateComplaint(
      selectedIssue.id,
      formData
    );
  }

  if (response.success) {
    closeModal();
  } else {
    alert(response.message);
  }
   } catch (error) {
    alert("Something went wrong");
  } finally {
    setLoading(false);
  }
};

  // ── Open modals ───────────────────────────────────────────────────────────
  const openCreateModal = () => {
    resetFormState();
    setModalType("create");
    setShowModal(true);
  };

const openEditModal = (issue) => {
  setModalType("edit");
  setSelectedIssue(issue);

  let assigned = [];

  if (issue.against_user_id) {
    try {
      assigned =
        typeof issue.against_user_id === "string"
          ? JSON.parse(issue.against_user_id)
          : issue.against_user_id;
    } catch {
      assigned = [];
    }
  }

  setForm({
    title: issue.title || "",
    description: issue.description || "",
    category: issue.complaint_type_id || "",

    priority: issue.priority || "medium",
    status: issue.status || "open",

    reportedBy: issue.raised_by || "",

    assignedTo: Array.isArray(assigned)
      ? assigned.map(Number)
      : [],

    // 🔥 FIX: store existing file
    existingFile: issue.file_path || null,

    attachments: [],

    comments: "",
    isAnonymous: issue.is_anonymous == 1,
    is_anonymous: Number(issue.is_anonymous) === 1,
    is_posh: Number(issue.is_posh) === 1,
  });

  setShowModal(true);
};

  const openDeleteModal = (issue) => {
    setModalType("delete");
    setSelectedIssue(issue);
    setShowModal(true);
  };

  // ── Pagination buttons ────────────────────────────────────────────────────
  const paginationButtons = () => {
    const start = Math.max(page - 1, 1);
    const end = Math.min(start + 2, totalPages);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i).map(
      (n) => (
        <button
          key={n}
          onClick={() => setPage(n)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            n === page
              ? "text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
          style={n === page ? { background: "linear-gradient(135deg, #2563eb, #d12465)" } : undefined}
        >
          {n}
        </button>
      )
    );
  };

  // ── Get category icon ─────────────────────────────────────────────────────
  const getCategoryIcon = (category) => {
    const IconComponent = CATEGORY_ICONS[category] || AlertTriangle;
    return <IconComponent size={16} className="text-gray-600" />;
  };

  const [deleting, setDeleting] = useState(false);

const handleDeleteComplaint = async () => {
  if (!selectedIssue) return;

  setDeleting(true);

  const user = JSON.parse(localStorage.getItem("user"));

  const res = await deleteComplaint(
    selectedIssue.id,
    {
      delete_audit_id: user?.id, // change to user?.user_id if needed
    }
  );

  setDeleting(false);

  if (res.success) {
    closeModal();
  } else {
    alert(res.message || "Failed to delete complaint");
  }
};


const openViewModal = (issue) => {
  setModalType("view");
  setSelectedIssue(issue);

  let assigned = [];

  if (issue.against_user_id) {
    try {
      assigned =
        typeof issue.against_user_id === "string"
          ? JSON.parse(issue.against_user_id)
          : issue.against_user_id;
    } catch {
      assigned = [];
    }
  }

  setForm({
    title: issue.title || "",
    description: issue.description || "",
    category: issue.complaint_type_id || "",
    priority: issue.priority || "medium",
    status: issue.status || "open",
    reportedBy: issue.raised_by || "",
    assignedTo: Array.isArray(assigned)
      ? assigned.map(Number)
      : [],
    existingFile: issue.file_path || null,
    attachments: [],
    comments: "",
    is_anonymous: Number(issue.is_anonymous) === 1,
    is_posh: Number(issue.is_posh) === 1,
  });

  setShowModal(true);
};
  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="bg-sky-50 min-h-screen">
        <div className="px-6 py-6 sm:px-9 max-sm:px-4">
          <div className="mb-5">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
              Workplace Issues Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Monitor, track and resolve workplace concerns and incidents across all departments.
            </p>
          </div>

          {/* ── Filters ── */}
         {/* ── Filters ── */}
<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-5 bg-white rounded-2xl border border-gray-100 px-4 py-4 shadow-md">
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap lg:flex-1 lg:items-center gap-3">
    <input
      type="text"
      placeholder="Search by category, title, description"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none w-full sm:col-span-2 lg:flex-1 lg:min-w-[160px] focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
    />
    <select
      value={filterCategory}
      onChange={(e) => setFilterCategory(e.target.value)}
      className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none w-full lg:w-auto lg:shrink-0 focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
    >
      <option value="All">All Categories</option>
      {complaintTypes
        .filter((item) => item.status === "active")
        .map((item) => (
          <option key={item.id} value={item.id}>
            {item.type_name}
          </option>
        ))}
    </select>
    <select
      value={filterPriority}
      onChange={(e) => setFilterPriority(e.target.value)}
      className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none w-full lg:w-auto lg:shrink-0 focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
    >
      <option value="All">All Priorities</option>
      <option value="low">Low Priority</option>
      <option value="medium">Medium Priority</option>
      <option value="high">High Priority</option>
    </select>
    <input
      type="date"
      value={fromDate}
      onChange={(e) => setFromDate(e.target.value)}
      className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none w-full lg:w-auto lg:shrink-0 focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
    />
    <input
      type="date"
      value={toDate}
      onChange={(e) => setToDate(e.target.value)}
      className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none w-full lg:w-auto lg:shrink-0 focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
    />
  </div>

  <div className="flex flex-col sm:flex-row sm:items-center gap-3 lg:shrink-0">
    {(searchTerm ||
      filterStatus.length > 0 ||
      filterCategory !== "All" ||
      filterPriority !== "All" ||
      fromDate ||
      toDate) && (
      <button
        type="button"
        onClick={() => {
          setSearchTerm("");
          setFilterStatus([]);
          setFilterCategory("All");
          setFilterPriority("All");
          setFromDate("");
          setToDate("");
        }}
        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap"
      >
        <X size={14} />
        Clear Filters
      </button>
    )}

    {canDo("WorkplaceIssues", "Add") && (
      <button
        onClick={openCreateModal}
        className="flex items-center justify-center gap-1.5 text-white px-6 py-2 rounded-lg text-sm font-medium transition w-full sm:w-auto whitespace-nowrap hover:opacity-90"
        style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
      >
        <Plus size={16} />
        Report Issue
      </button>
    )}
  </div>
</div>

          {/* ── Status summary cards (clickable filter) ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {STATUS_OPTIONS.map((status) => {
              const active = filterStatus.includes(status);
              return (
                <div
                  key={status}
                  onClick={() =>
                    setFilterStatus((prev) =>
                      prev.includes(status)
                        ? prev.filter((s) => s !== status)
                        : [...prev, status]
                    )
                  }
                  className={`cursor-pointer p-3.5 rounded-2xl border bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col items-center justify-center gap-1.5
                    ${active ? "border-transparent ring-2 ring-[#2563eb]" : "border-gray-100"}`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[status] || "bg-gray-400"}`} aria-hidden="true" />
                    <div className="text-xs font-semibold text-gray-600 text-center leading-tight uppercase tracking-wide">
                      {STATUS_LABEL[status] || status}
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-gray-800">
                    {statusCounts[status] || 0}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Table ── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-4 sm:p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-sky-50">
                    {[
                      "ID",
                      "Category",
                      "Title",
                      "Description",
                      "Reporter",
                      "Priority",
                      "Status",
                      "Reported To",
                      ...(canDo("WorkplaceIssues", "View") ||
                      canDo("WorkplaceIssues", "Edit") ||
                      canDo("WorkplaceIssues", "Delete")
                        ? ["Actions"]
                        : []),
                      "Last Updated",
                    ].map((h) => (
                      <th
                        key={h}
                        className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentRows.length > 0 ? (
                    currentRows.map((issue) => (
                      <tr
                        key={issue.id}
                        className="border-b border-gray-50 hover:bg-gray-50 transition-colors duration-150"
                      >
                        <td className="py-3 px-4 text-indigo-600 font-medium">
                          {issue.id}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {/* {getCategoryIcon(issue.type_name)} */}
                            <span className="text-gray-800 capitalize">{issue.type_name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-800 max-w-[200px]">
                          <div>{issue.title}</div>
                        </td>
                        <td className="py-3 px-4 text-gray-600 max-w-[220px]">
                          <CommentCell text={issue.description} />
                        </td>
                        <td className="py-3 px-4">
  {isAdmin ? (
    <>
      {/* ADMIN: always show name */}
      <Avatar name={issue.raised_by_name} />

      {/* POSH badge only */}
      {issue.is_posh == 1 && (
        <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold">
          POSH
        </span>
      )}
    </>
  ) : (
    <>
      {/* NON-ADMIN: POSH hides identity */}
      {issue.is_posh == 1 || issue.is_anonymous == 1 ? (
        <span className="text-gray-500 italic text-sm">
          Anonymous
        </span>
      ) : (
        <Avatar name={issue.raised_by_name} />
      )}

      {issue.is_posh == 1 && (
        <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold">
          POSH
        </span>
      )}
    </>
  )}
</td>
                        {/* <td className="py-3 px-4 text-gray-800">
                          {issue.department}
                        </td> */}
                        <td className="py-3 px-4">
                          <span
                            className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full ${
                              PRIORITY_CLASS[issue.priority] || "bg-gray-100 text-gray-700"
                            }`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                PRIORITY_DOT[issue.priority] || "bg-gray-400"
                              }`}
                              aria-hidden="true"
                            />
                            {(issue.priority || "-").toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full ${
                              STATUS_CLASS[issue.status] || "bg-gray-100 text-gray-700"
                            }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[issue.status] || "bg-gray-400"}`} aria-hidden="true" />
                            {STATUS_LABEL[issue.status] || issue.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex -space-x-2">
                            {(issue.against_name || "")
                              .split(",")
                              .map((name, index) => (
                                <Avatar key={index} name={name.trim()} />
                              ))}
                          </div>
                        </td>
                        {(canDo("WorkplaceIssues", "View") ||
  canDo("WorkplaceIssues", "Edit") ||
  canDo("WorkplaceIssues", "Delete")) && (
  <td className="py-3 px-4 text-center">
    <div className="flex justify-center gap-3 text-gray-600">
      {canDo("WorkplaceIssues", "View") && (
        <Eye
  size={16}
  className="cursor-pointer hover:text-indigo-600"
  title="View Details"
  onClick={() => openViewModal(issue)}
/>
      )}

      {canDo("WorkplaceIssues", "Edit") && (
        <Edit
          size={16}
          className="cursor-pointer hover:text-indigo-600"
          onClick={() => openEditModal(issue)}
        />
      )}

      {canDo("WorkplaceIssues", "Delete") && (
        <Trash2
          size={16}
          className="cursor-pointer hover:text-red-600"
          onClick={() => openDeleteModal(issue)}
        />
      )}
    </div>
  </td>
)}
                        <td className="py-3 px-4 text-gray-600 whitespace-nowrap">
                          {issue.update_audit_time || issue.create_audit_time}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center py-10 text-gray-400 text-sm">
                        No workplace issues found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ── */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 pt-4 border-t border-gray-100 gap-3">
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <span>Rows per page:</span>
                <select
                  className="border border-gray-200 bg-gray-50 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[5, 10, 15].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronsLeft size={16} />
                </button>
                <button
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  disabled={page === 1}
                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <MdKeyboardArrowLeft size={16} />
                </button>
                {paginationButtons()}
                <button
                  onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  disabled={page === totalPages}
                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <MdKeyboardArrowRight size={16} />
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronsRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Create / Edit / Delete Modal ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className={`bg-white rounded-2xl w-full shadow-2xl relative max-h-[88vh] flex flex-col overflow-hidden ${modalType === "delete" ? "max-w-lg max-sm:max-w-sm p-6" : "max-w-2xl max-sm:max-w-sm"}`}>
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 z-10"
            >
              <X size={20} />
            </button>

            {modalType === "delete" ? (
              <>
                <h3 className="text-lg font-semibold mb-4 text-gray-800">
                  Delete Issue
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Are you sure you want to delete{" "}
                  <span className="font-semibold">{selectedIssue?.title}</span>?
                </p>
                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 w-full sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteComplaint}
                    disabled={deleting}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 w-full sm:w-auto"
                  >
                    {deleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col min-h-0 max-h-[88vh]">
                {/* Sticky header */}
                <div className="flex items-center gap-3 px-6 sm:px-7 pt-6 sm:pt-7 pb-4 border-b border-gray-100 shrink-0">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0"
                    style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
                  >
                    {modalType === "create" ? <Plus size={20} /> : <Edit size={18} />}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
  {modalType === "create"
    ? "Report New Issue"
    : modalType === "view"
    ? "View Issue"
    : "Edit Issue"}
</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
  {modalType === "create"
    ? "Report a workplace concern or incident for investigation"
    : modalType === "view"
    ? "View workplace issue details"
    : "Update the details of this workplace issue"}
</p>
                  </div>
                </div>

                {/* Scrollable body */}
                <div className="px-6 sm:px-7 py-5 space-y-5 overflow-y-auto flex-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Category */}
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                        <AlertTriangle size={14} className="text-gray-400" />
                        Category
                      </label>
                      <select
                        name="category"
                        disabled={isView}
                        value={form.category}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
                      >
                        <option value="">Select Category</option>

                        {complaintTypes.filter((item) => item.status === "active").map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.type_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Priority */}
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                        <AlertTriangle size={14} className="text-gray-400" />
                        Priority
                      </label>
                      <select
                        name="priority"
                        disabled={isView}
                        value={form.priority}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
                      >
                        <option value="low">Low Priority</option>
                        <option value="medium">Medium Priority</option>
                        <option value="high">High Priority</option>
                      </select>
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                      <MessageSquare size={14} className="text-gray-400" />
                      Issue Title
                    </label>
                    <input
                      type="text"
                      readOnly={isView}
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                      required
                      placeholder="Brief, descriptive title of the issue"
                      className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                      <MessageSquare size={14} className="text-gray-400" />
                      Description
                    </label>
                    <textarea
                      name="description"
                      readOnly={isView}
                      rows="4"
                      value={form.description}
                      onChange={handleChange}
                      required
                      placeholder="Provide detailed information about the incident or concern"
                      className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Department */}
                    {/* <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                        <User size={14} className="text-gray-400" />
                        Department
                      </label>

                      <select
                        name="department"
                        value={form.department}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 focus:bg-white transition"
                      >
                        <option value="">Select Department</option>

                        {departments.filter((department) => department.status === "active").map((department) => (
                          <option key={department.id} value={department.id}>
                            {department.name}
                          </option>
                        ))}
                      </select>
                    </div> */}

                    {/* Assigned To */}
                    <div className="relative">
                      <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                        <User size={14} className="text-gray-400" />
                        Assigned To
                      </label>

                      <button
                        type="button"
                        disabled={isView}
                        onClick={() => setShowEmployeeDropdown(!showEmployeeDropdown)}
                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
                      >
                        <span className="truncate text-left">
                          {form.assignedTo.length > 0
                            ? allEmployees
                                .filter((emp) => form.assignedTo.includes(emp.id))
                                .map((emp) => emp.full_name)
                                .join(", ")
                            : "Select Employees"}
                        </span>

                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {showEmployeeDropdown && (
                        <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          {allEmployees.map((employee) => (
                            <label
                              key={employee.id}
                              className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={form.assignedTo.includes(employee.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setForm((prev) => ({
                                      ...prev,
                                      assignedTo: [...prev.assignedTo, employee.id],
                                    }));
                                  } else {
                                    setForm((prev) => ({
                                      ...prev,
                                      assignedTo: prev.assignedTo.filter(
                                        (id) => id !== employee.id
                                      ),
                                    }));
                                  }
                                }}
                                className="accent-[#2563eb]"
                              />

                              <span className="text-sm">{employee.full_name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                        📎
                        Attach Files
                      </label>

                      <input
                        type="file"
                        name="attachments"
                        multiple
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            attachments: Array.from(e.target.files),
                          }))
                        }
                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm
                          file:rounded-lg file:border-0
                          file:text-sm file:font-medium
                          file:bg-indigo-50 file:text-indigo-700
                          hover:file:bg-indigo-100
                          focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40
                          focus:border-[#2563eb]/40 focus:bg-white transition"
                      />

                      {form.attachments?.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {form.attachments.map((file, index) => (
                            <div
                              key={index}
                              className="text-xs text-gray-600 bg-gray-100 rounded-lg px-3 py-1"
                            >
                              {file.name}
                            </div>
                          ))}
                        </div>
                      )}
                      {modalType === "edit" && form.existingFile && (
                        <div className="mt-2 text-xs text-gray-600 bg-gray-100 rounded-lg px-3 py-2 flex items-center justify-between">
                          <span>Existing file: {form.existingFile}</span>

                          <a
                            href={`${CDN_URL}/${form.existingFile}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-indigo-600 hover:underline"
                          >
                            View
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Flags */}
<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
  
  {/* Anonymous */}
  <label className="flex items-center gap-3 cursor-pointer">
    <input
      type="checkbox"
      disabled={isView}
      name="is_anonymous"
      checked={form.is_anonymous}
      onChange={(e) =>
        setForm((prev) => ({
          ...prev,
          is_anonymous: e.target.checked,
        }))
      }
      className="w-4 h-4 accent-[#2563eb]"
    />
    <span className="text-sm text-gray-700">Submit Anonymously</span>
  </label>

  {/* POSH */}
  <label className="flex items-center gap-3 cursor-pointer">
    <input
      type="checkbox"
      name="is_posh"
      disabled={isView}
      checked={form.is_posh}
      onChange={(e) =>
        setForm((prev) => ({
          ...prev,
          is_posh: e.target.checked,
        }))
      }
      className="w-4 h-4 accent-[#2563eb]"
    />
    <span className="text-sm text-gray-700">POSH Complaint</span>
  </label>

</div>

                  {/* Status pills */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
                      <Clock size={14} className="text-gray-400" />
                      Status
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      {STATUS_OPTIONS.map((s) => {
                        const label = STATUS_LABEL[s] || s;
                        const active = form.status === s;
                        const activeClass = STATUS_CLASS[s] || "bg-indigo-100 text-indigo-700";
                        return (
                          <label
                            key={s}
                            className={`cursor-pointer select-none px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                              active
                                ? `${activeClass} border-transparent ring-2 ring-offset-1 ring-[#2563eb]/60`
                                : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"
                            }`}
                          >
                            <input
                              type="radio"
                              name="status"
                              disabled={isView}
                              value={s}
                              checked={active}
                              onChange={handleChange}
                              className="hidden"
                            />
                            {label}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Comments */}
                  {/* <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                      <MessageSquare size={14} className="text-gray-400" />
                      Additional Comments <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <textarea
                      name="comments"
                      rows="3"
                      value={form.comments}
                      onChange={handleChange}
                      placeholder="Any additional notes or updates"
                      className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 focus:bg-white transition"
                    />
                  </div> */}
                </div>

                {/* Sticky footer */}
                <div className="flex flex-col sm:flex-row justify-end gap-3 px-6 sm:px-7 py-4 border-t border-gray-100 shrink-0">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 w-full sm:w-auto transition"
                  >
                    Cancel
                  </button>
                  {!isView && (
  <button
    type="submit"
    disabled={issueloading}
    style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
    className="px-5 py-2.5 rounded-xl text-sm font-medium w-full sm:w-auto flex items-center justify-center gap-2 text-white"
  >
    {loading ? (
      <>
        <Loader2 size={16} className="animate-spin" />
        Saving...
      </>
    ) : modalType === "create" ? (
      <>
        <Plus size={16} />
        Report Issue
      </>
    ) : (
      "Save Changes"
    )}
  </button>
)}
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}